// ----------------------------------------------------------------------
// <copyright file="Izenda.LazyLoading.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React, { Component, PropTypes } from 'react';
import ReactDOM from 'react-dom';
import LazyLoadingUIStore from './Izenda.LazyLoadingUIStore';
import connect from 'share/enhancers/Izenda.Connect';
import { observer, propTypes as MobxPropTypes } from 'mobx-react';
import Utils from 'share/utils/Izenda.DomUtils';
import classnames from 'classnames';
import LazyLoadingVirtualNode from './Izenda.LazyLoadingVirtualNode';
import LoadingBar from './Izenda.LazyLoadingBar';
import { invokeArgs, isNil, isEmpty, map, get } from 'lodash/fp';

@observer
export default class LazyLoading extends Component {
  static propTypes = {
    data: MobxPropTypes.arrayOrObservableArray.isRequired,
    defaultItemsNumber: PropTypes.number,
    viewHeight: PropTypes.number,
    itemHeight: PropTypes.number,
    getData: PropTypes.func.isRequired,
    isLastPage: PropTypes.bool.isRequired,
    registerLazyLoadingStore: PropTypes.func
  };

  static defaultProps = {
    itemKey: 'key'
  };

  scrollParent;

  constructor(props) {
    super(props);
    this.store = new LazyLoadingUIStore(props);
  }

  componentDidMount() {
    this.registerLazyLoadingStore();
    this.addEvent();

    if (this.store.training) {
      this.setViewHeight();
      this.getData();
    } else {
      this.endLoading();
    }
  }

  componentWillUnmount() {
    this.removeEvent();
  }

  componentWillReceiveProps({ data, isLastPage }) {
    this.store.updateData(data, isLastPage);
  }

  componentDidUpdate() {
    const { isLoading, isLastPage } = this.store;
    this.store.removeVirtualNode();
    !isLoading && this.loadVirtualNode();
    isLastPage && this.endLoading();
  }

  endLoading() {
    this.props.loader && this.props.loader.endLoading();
  }

  registerLazyLoadingStore() {
    invokeArgs('registerLazyLoadingStore', [this.store], this.props);
  }

  setViewHeight() {
    const { viewHeight } = this.props;
    if (viewHeight) {
      this.store.setViewHeight(viewHeight);
    } else {
      // don't use clientHeight here, because we use custom scroll bar.
      const calViewHeight = isNil(this.scrollParent) ? 0 : this.scrollParent.offsetHeight - this.getScrollbarWidth();
      this.store.setViewHeight(calViewHeight);
    }
  }

  getScrollbarWidth() {
    let scrollbarWidth = 0;

    if (typeof document !== 'undefined') {
      let div = document.createElement('div');
      div.className = 'izenda-ScrollBox-WidthMeasure';
      document.body.appendChild(div);
      scrollbarWidth = div.offsetWidth - div.clientWidth;
      document.body.removeChild(div);
    }

    return scrollbarWidth;
  }

  getData(node) {
    const { getData } = this.props;
    getData(this.store.getPageInfo(node));
  }

  addEvent() {
    this.scrollParent &&
      this.scrollParent.addEventListener('scroll', this.loadVirtualNode, {
        capture: false,
        passive: true
      });
  }

  removeEvent() {
    this.scrollParent &&
      this.scrollParent.removeEventListener('scroll', this.loadVirtualNode, {
        capture: false,
        passive: true
      });
  }

  onScroll = () => {
    this.loadVirtualNode();
  };

  loadVirtualNode = () => {
    const store = this.store;
    const node = store.getVirtualNode(this.scrollParent);
    if (!isNil(node)) {
      store.showLoading();
      store.setRenderingNode(node);
      this.getData(node);
    } else {
      this.endLoading();
    }
  };

  renderLazyLoadingItemList() {
    const store = this.store;
    const { renderItem, itemClassName, itemKey } = this.props;
    const items = store.getItemsOnViewScreen;
    store.resetVirtualNodes();

    if (!isEmpty(items)) {
      return map(item => {
        if (get('data.source.isVirtualNode', item)) {
          store.virtualNodes.push(item.data.source);
          return (
            <LazyLoadingVirtualNode key={item.key} store={store} source={item.data.source} className={itemClassName} />
          );
        } else if (get('isVirtualNode', item)) {
          store.virtualNodes.push(item);
          return <LazyLoadingVirtualNode key={item.id} store={store} source={item} className={itemClassName} />;
        } else {
          return (
            <LazyLoadingItem
              store={store}
              getData={this.getData}
              className={itemClassName}
              key={item[itemKey]}
              item={item}
            >
              {renderItem(item, store.pageSize, this.loadVirtualNode)}
            </LazyLoadingItem>
          );
        }
      }, items);
    }
  }

  setDom = dom => {
    dom && (this.scrollParent = Utils.findScrollableParent(dom));
  };

  render() {
    return (
      <div ref={this.setDom} className={classnames('izenda-LazyLoading', this.props.className)}>
        {this.renderLazyLoadingItemList()}
        <LoadingBar store={this.store} className="u_inlineBlock u_fullWidth" />
      </div>
    );
  }
}

@observer
class LazyLoadingItem extends Component {
  static propTypes = {
    store: PropTypes.object.isRequired
  };

  shouldComponentUpdate(nextProps) {
    return nextProps.item !== this.props.item;
  }

  componentDidMount() {
    const itemDom = ReactDOM.findDOMNode(this.itemDom);
    const { store } = this.props;
    if (store.training) {
      store.setItemHeight(itemDom.offsetHeight);
      store.training = false;
    }
  }

  setDom = dom => {
    this.itemDom = dom;
  };

  render() {
    return (
      <div ref={this.setDom} className={classnames('izenda-LazyLoadingItem', this.props.className)}>
        {this.props.children}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/share/components/controls/LazyLoading/Izenda.LazyLoading.js